import { Injectable, inject } from '@angular/core';
import { ChatMessage } from '../models/chat-message';
import { AuthService } from './auth.service';
import { ChatPersistenceService, ChatThread, ChatThreadSync } from './chat-persistence.service';

type RemoteThread = {
  id: string;
  title: string;
  related_topics: unknown;
  titled_key: string | null;
  created_at: string;
  updated_at: string;
};

type RemoteMessage = {
  id: string;
  thread_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata: unknown;
  created_at: string;
};

/** Keeps the browser cache and the authenticated user's Supabase chats aligned. */
@Injectable({ providedIn: 'root' })
export class ChatSyncService {
  private readonly auth = inject(AuthService);
  private readonly persistence = inject(ChatPersistenceService);
  private writeQueue = Promise.resolve();

  async start(): Promise<void> {
    await this.auth.waitUntilInitialized();
    const client = this.auth.supabase;
    const user = this.auth.user();
    if (!client || !user) return;

    const cached = this.persistence.threads();
    const [{ data: remoteThreads, error: threadsError }, { data: remoteMessages, error: messagesError }] = await Promise.all([
      client.from('chat_threads').select('id,title,related_topics,titled_key,created_at,updated_at').eq('user_id', user.id).order('updated_at', { ascending: false }),
      client.from('chat_messages').select('id,thread_id,role,content,metadata,created_at').eq('user_id', user.id).order('created_at', { ascending: true }),
    ]);
    if (threadsError || messagesError) {
      console.warn('Could not load synced chats.', threadsError?.message ?? messagesError?.message);
      return;
    }

    const remote = this.toThreads((remoteThreads ?? []) as RemoteThread[], (remoteMessages ?? []) as RemoteMessage[]);
    const remoteIds = new Set(remote.map((thread) => thread.id));
    this.persistence.hydrateThreads(this.mergeThreads(cached, remote));
    this.persistence.setThreadSync(this.syncFor(user.id));

    // First-time migration: preserve conversations that existed before sync was enabled.
    for (const thread of cached) {
      const hydrated = this.persistence.threads().find((candidate) => candidate.id === thread.id);
      if (!remoteIds.has(thread.id) && hydrated) this.enqueue(() => this.writeThread(user.id, hydrated));
    }
  }

  private syncFor(userId: string): ChatThreadSync {
    return {
      upsert: (thread) => this.enqueue(() => this.writeThread(userId, thread)),
      delete: (threadId) => this.enqueue(() => this.deleteThread(userId, threadId)),
      clear: () => this.enqueue(() => this.clearThreads(userId)),
    };
  }

  private enqueue(work: () => Promise<void>): void {
    this.writeQueue = this.writeQueue.then(work).catch((error: unknown) => {
      console.warn('Could not sync chat changes.', error);
    });
  }

  private async writeThread(userId: string, thread: ChatThread): Promise<void> {
    const client = this.auth.supabase;
    if (!client || this.auth.user()?.id !== userId) return;
    const { error: threadError } = await client.from('chat_threads').upsert({
      id: thread.id,
      user_id: userId,
      title: thread.title,
      related_topics: thread.relatedTopics ?? null,
      titled_key: thread.titledKey ?? null,
      created_at: thread.createdAt,
      updated_at: thread.updatedAt,
    });
    if (threadError) throw new Error(threadError.message);

    if (thread.messages.length === 0) return;
    const { error: messagesError } = await client.from('chat_messages').upsert(thread.messages.map((message) => ({
      id: message.id,
      thread_id: thread.id,
      user_id: userId,
      role: message.author === 'bot' ? 'assistant' : 'user',
      content: message.text,
      metadata: this.messageMetadata(message),
    })));
    if (messagesError) throw new Error(messagesError.message);
  }

  private async deleteThread(userId: string, threadId: string): Promise<void> {
    const client = this.auth.supabase;
    if (!client || this.auth.user()?.id !== userId) return;
    const { error } = await client.from('chat_threads').delete().eq('id', threadId).eq('user_id', userId);
    if (error) throw new Error(error.message);
  }

  private async clearThreads(userId: string): Promise<void> {
    const client = this.auth.supabase;
    if (!client || this.auth.user()?.id !== userId) return;
    const { error } = await client.from('chat_threads').delete().eq('user_id', userId);
    if (error) throw new Error(error.message);
  }

  private toThreads(threads: RemoteThread[], messages: RemoteMessage[]): ChatThread[] {
    const messagesByThread = new Map<string, ChatMessage[]>();
    for (const message of messages) {
      const list = messagesByThread.get(message.thread_id) ?? [];
      list.push(this.toMessage(message));
      messagesByThread.set(message.thread_id, list);
    }
    return threads.map((thread) => ({
      id: thread.id,
      title: thread.title,
      messages: messagesByThread.get(thread.id) ?? [],
      relatedTopics: Array.isArray(thread.related_topics) && thread.related_topics.every((topic) => typeof topic === 'string')
        ? thread.related_topics as string[]
        : undefined,
      titledKey: thread.titled_key ?? undefined,
      createdAt: thread.created_at,
      updatedAt: thread.updated_at,
    }));
  }

  private toMessage(message: RemoteMessage): ChatMessage {
    const metadata = message.metadata && typeof message.metadata === 'object' ? message.metadata as Partial<ChatMessage> : {};
    return {
      id: message.id,
      author: message.role === 'assistant' ? 'bot' : 'user',
      text: message.content,
      time: typeof metadata.time === 'string' ? metadata.time : new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(message.created_at)),
      ...(typeof metadata.thinking === 'string' ? { thinking: metadata.thinking } : {}),
      ...(Array.isArray(metadata.attachments) ? { attachments: metadata.attachments } : {}),
      ...(metadata.toolInvocation && typeof metadata.toolInvocation === 'object' ? { toolInvocation: metadata.toolInvocation as ChatMessage['toolInvocation'] } : {}),
    };
  }

  private messageMetadata(message: ChatMessage): Record<string, unknown> {
    return {
      time: message.time,
      ...(message.thinking ? { thinking: message.thinking } : {}),
      ...(message.attachments ? { attachments: message.attachments } : {}),
      ...(message.toolInvocation ? { toolInvocation: message.toolInvocation } : {}),
    };
  }

  private mergeThreads(cached: readonly ChatThread[], remote: ChatThread[]): ChatThread[] {
    const merged = new Map(remote.map((thread) => [thread.id, thread]));
    for (const thread of cached) {
      const existing = merged.get(thread.id);
      if (!existing || thread.updatedAt > existing.updatedAt) merged.set(thread.id, thread);
    }
    return [...merged.values()];
  }
}
